import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, FileText, Pencil, Download, Printer, Plus, CreditCard,
  CheckCircle, Clock, AlertTriangle, Calendar, Building, User,
  Loader2, Trash2, RefreshCw, X, Search, ChevronsUpDown, Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { facturasAPI, contactosAPI, expedientesAPI, configuracionAPI } from '../api';
import { toast } from '../hooks/use-toast';
import VerifactuPanel from '../components/Verifactu/VerifactuPanel';
import EnviarFacturaEmailDialog from '../components/EnviarFacturaEmailDialog';
import FiscalStateBadge from '../components/Verifactu/FiscalStateBadge';
import CotitularesSelector from '../components/CotitularesSelector';

const ESTADOS = {
  pendiente: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  parcial: { label: 'Pago Parcial', color: 'bg-blue-100 text-blue-800', icon: CreditCard },
  pagada: { label: 'Pagada', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  vencida: { label: 'Vencida', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  anulada: { label: 'Anulada', color: 'bg-gray-100 text-gray-800', icon: X },
  rectificada: { label: 'Rectificada', color: 'bg-purple-100 text-purple-800', icon: AlertTriangle },
};

const TIPOS = {
  proforma: { label: 'Proforma', color: 'text-blue-600' },
  factura: { label: 'Factura', color: 'text-green-600' },
  presupuesto: { label: 'Presupuesto', color: 'text-purple-600' },
  rectificativa: { label: 'Factura rectificativa', color: 'text-amber-600' },
};

const formatCurrency = (amount) => {
  const n = parseFloat(amount);
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(Number.isFinite(n) ? n : 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Combobox buscable para clientes
const ClienteComboboxDetalle = ({ value, onChange, contactos: initialContactosProp = [], onSelectedChange }) => {
  const initialContactos = Array.isArray(initialContactosProp) ? initialContactosProp : [];
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [stickySelected, setStickySelected] = React.useState(null);
  const searchTimeout = React.useRef(null);
  const inputRef = React.useRef(null);

  const displayList = searchQuery.length >= 2 ? (Array.isArray(searchResults) ? searchResults : []) : initialContactos;
  const allKnown = React.useMemo(() => {
    const map = new Map();
    const safeContactos = Array.isArray(initialContactos) ? initialContactos : [];
    const safeResults = Array.isArray(searchResults) ? searchResults : [];
    safeContactos.forEach(c => { if (c && c.id != null) map.set(String(c.id), c); });
    safeResults.forEach(c => { if (c && c.id != null) map.set(String(c.id), c); });
    if (stickySelected && stickySelected.id != null) map.set(String(stickySelected.id), stickySelected);
    return map;
  }, [initialContactos, searchResults, stickySelected]);
  const selected = value ? allKnown.get(String(value)) : null;

  React.useEffect(() => { if (onSelectedChange) onSelectedChange(selected || null); }, [selected, onSelectedChange]);

  // Si llega value desde fuera y no lo conocemos, buscarlo
  React.useEffect(() => {
    if (!value) { setStickySelected(null); return; }
    if (allKnown.get(String(value))) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await contactosAPI.getById(value);
        const c = res.data?.data || res.data;
        if (!cancelled && c && c.id) setStickySelected(c);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [value]);

  React.useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (query.length < 2) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await contactosAPI.getAll({ search: query, limit: 50 });
        const data = res.data?.data || res.data || [];
        setSearchResults(Array.isArray(data) ? data : []);
      } catch (e) { console.error('Error buscando clientes:', e); }
      setSearching(false);
    }, 300);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className={`w-full justify-between font-normal ${selected ? 'border-green-500 bg-green-50 text-green-900 hover:bg-green-100' : ''}`} type="button">
          <span className="flex items-center gap-2 truncate">
            {selected && <Check className="h-4 w-4 text-green-600 shrink-0" />}
            <span className="truncate">{selected ? `${selected.nombre_completo || selected.nombre}${selected.nif_cif ? ` (${selected.nif_cif})` : ''}` : 'Buscar cliente...'}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="flex items-center border-b px-3 py-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input ref={inputRef} type="text" className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
            placeholder="Buscar por nombre o NIF..." value={searchQuery} onChange={(e) => handleSearch(e.target.value)} />
          {searchQuery && <button type="button" className="ml-1 text-gray-400 hover:text-gray-600 text-xs" onClick={() => { setSearchQuery(''); setSearchResults([]); }}>&times;</button>}
        </div>
        <div className="max-h-[280px] overflow-y-auto">
          {searching && <div className="p-3 text-sm text-center text-gray-500">Buscando...</div>}
          {!searching && displayList.length === 0 && <div className="p-4 text-sm text-center text-gray-500">{searchQuery.length < 2 ? 'Escribe al menos 2 caracteres...' : 'No se encontraron clientes'}</div>}
          {displayList.map(c => (
            <div key={c.id} className={`flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors ${String(value) === String(c.id) ? 'bg-accent/50' : ''}`}
              onClick={() => { setStickySelected(c); onChange(String(c.id)); setOpen(false); setSearchQuery(''); setSearchResults([]); }}>
              <Check className={`mr-2 h-4 w-4 flex-shrink-0 ${String(value) === String(c.id) ? 'opacity-100' : 'opacity-0'}`} />
              <span className="truncate">{c.nombre_completo || c.nombre}{c.nif_cif ? ` (${c.nif_cif})` : ''}</span>
            </div>
          ))}
        </div>
        {value && <div className="border-t px-3 py-2"><button type="button" className="text-xs text-red-500 hover:text-red-700" onClick={() => { setStickySelected(null); onChange(''); setOpen(false); setSearchQuery(''); setSearchResults([]); }}>Quitar cliente</button></div>}
      </PopoverContent>
    </Popover>
  );
};

// Combobox buscable para expedientes
const ExpedienteComboboxDetalle = ({ value, onChange, expedientes: initialExpedientesProp = [], onSelectedChange }) => {
  const initialExpedientes = Array.isArray(initialExpedientesProp) ? initialExpedientesProp : [];
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [stickySelected, setStickySelected] = React.useState(null);
  const searchTimeout = React.useRef(null);
  const inputRef = React.useRef(null);

  const displayList = searchQuery.length >= 2 ? (Array.isArray(searchResults) ? searchResults : []) : initialExpedientes;
  const allKnown = React.useMemo(() => {
    const map = new Map();
    const safeExpedientes = Array.isArray(initialExpedientes) ? initialExpedientes : [];
    const safeResults = Array.isArray(searchResults) ? searchResults : [];
    safeExpedientes.forEach(e => { if (e && e.id != null) map.set(String(e.id), e); });
    safeResults.forEach(e => { if (e && e.id != null) map.set(String(e.id), e); });
    if (stickySelected && stickySelected.id != null) map.set(String(stickySelected.id), stickySelected);
    return map;
  }, [initialExpedientes, searchResults, stickySelected]);
  const selected = value ? allKnown.get(String(value)) : null;

  React.useEffect(() => { if (onSelectedChange) onSelectedChange(selected || null); }, [selected, onSelectedChange]);

  React.useEffect(() => {
    if (!value) { setStickySelected(null); return; }
    if (allKnown.get(String(value))) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await expedientesAPI.getById(value);
        const exp = res.data?.data || res.data;
        if (!cancelled && exp && exp.id) setStickySelected(exp);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [value]);

  React.useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (query.length < 2) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await expedientesAPI.getAll({ search: query, limit: 50 });
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.expedientes || []);
        setSearchResults(data);
      } catch (e) { console.error('Error buscando expedientes:', e); }
      setSearching(false);
    }, 300);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className={`w-full justify-between font-normal ${selected ? 'border-green-500 bg-green-50 text-green-900 hover:bg-green-100' : ''}`} type="button">
          <span className="flex items-center gap-2 truncate">
            {selected && <Check className="h-4 w-4 text-green-600 shrink-0" />}
            <span className="truncate">{selected ? `${selected.numero_expediente} - ${selected.titulo}` : 'Buscar expediente...'}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="flex items-center border-b px-3 py-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input ref={inputRef} type="text" className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
            placeholder="Buscar por numero o nombre..." value={searchQuery} onChange={(e) => handleSearch(e.target.value)} />
          {searchQuery && <button type="button" className="ml-1 text-gray-400 hover:text-gray-600 text-xs" onClick={() => { setSearchQuery(''); setSearchResults([]); }}>&times;</button>}
        </div>
        <div className="max-h-[280px] overflow-y-auto">
          {searching && <div className="p-3 text-sm text-center text-gray-500">Buscando...</div>}
          {!searching && displayList.length === 0 && <div className="p-4 text-sm text-center text-gray-500">{searchQuery.length < 2 ? 'Escribe al menos 2 caracteres...' : 'No se encontraron expedientes'}</div>}
          {displayList.map(exp => (
            <div key={exp.id} className={`flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors ${String(value) === String(exp.id) ? 'bg-accent/50' : ''}`}
              onClick={() => { setStickySelected(exp); onChange(String(exp.id), exp); setOpen(false); setSearchQuery(''); setSearchResults([]); }}>
              <Check className={`mr-2 h-4 w-4 flex-shrink-0 ${String(value) === String(exp.id) ? 'opacity-100' : 'opacity-0'}`} />
              <span className="truncate">{exp.numero_expediente} - {exp.titulo}</span>
            </div>
          ))}
        </div>
        {value && <div className="border-t px-3 py-2"><button type="button" className="text-xs text-red-500 hover:text-red-700" onClick={() => { setStickySelected(null); onChange(''); setOpen(false); setSearchQuery(''); setSearchResults([]); }}>Quitar expediente</button></div>}
      </PopoverContent>
    </Popover>
  );
};

const FacturaDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [factura, setFactura] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialogs
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPagoDialogOpen, setIsPagoDialogOpen] = useState(false);
  // Emisión de factura rectificativa (art. 6.1.a RD 1619/2012)
  const [isRectDialogOpen, setIsRectDialogOpen] = useState(false);
  const [rectForm, setRectForm] = useState({
    motivo: '',
    tipo_rectificativa: 'S',
    fecha_emision: new Date().toISOString().split('T')[0],
    copiar_conceptos: true,
    invertir_importes: false,
  });
  
  // Datos para edición
  const [contactos, setContactos] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  // Objetos seleccionados (para mostrar tarjeta de datos asociados)
  const [selectedClienteData, setSelectedClienteData] = useState(null);
  const [selectedExpedienteData, setSelectedExpedienteData] = useState(null);
  
  // Form de pago
  const [pagoForm, setPagoForm] = useState({
    importe: '',
    metodo_pago: 'TRANSFERENCIA BANCARIA',
    referencia: '',
    descripcion: ''
  });
  
  // Form de edición
  const [editForm, setEditForm] = useState(null);

  // Datos de emisión (facturador) de esta factura
  const [emisor, setEmisor] = useState(null);
  const [emisorOrigen, setEmisorOrigen] = useState(null);
  const [logoEmisor, setLogoEmisor] = useState(null);
  const [emisorIncompleto, setEmisorIncompleto] = useState([]);
  const [envioEmail, setEnvioEmail] = useState(null);
  const [facturadores, setFacturadores] = useState([]);
  const [facturadorSel, setFacturadorSel] = useState('');
  const [aplicarSerie, setAplicarSerie] = useState(false);
  const [cambiarEmisor, setCambiarEmisor] = useState(false);
  const [serieSinFacturador, setSerieSinFacturador] = useState(0);
  const [asignando, setAsignando] = useState(false);

  useEffect(() => {
    loadFactura();
    loadRelatedData();
    loadEmisor();
    loadEnvioEmail();
  }, [id]);

  const asignarFacturador = async () => {
    if (!facturadorSel) {
      toast({ title: 'Elige un facturador', variant: 'destructive' });
      return;
    }
    setAsignando(true);
    try {
      const res = await facturasAPI.asignarFacturador(id, {
        facturador_id: Number(facturadorSel),
        aplicar_a_la_serie: aplicarSerie,
      });
      const n = res.data?.facturas_serie_actualizadas || 0;
      toast({
        title: 'Facturador asignado',
        description: n > 1 ? `Se han actualizado ${n} facturas de esta serie` : undefined,
      });
      setCambiarEmisor(false);
      setAplicarSerie(false);
      await loadEmisor();
    } catch (e) {
      toast({
        title: 'No se pudo asignar',
        description: e?.response?.data?.detail || 'Inténtalo de nuevo',
        variant: 'destructive',
      });
    } finally {
      setAsignando(false);
    }
  };

  const selectorFacturador = (
    <div className="mt-3 space-y-2" data-testid="factura-asignar-facturador">
      <Label className="text-xs">Facturador emisor</Label>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={facturadorSel}
          onChange={(e) => setFacturadorSel(e.target.value)}
          className="border rounded-md px-2 py-1 text-sm bg-white"
          data-testid="factura-facturador-select"
        >
          <option value="">— Elegir —</option>
          {facturadores.map((f) => (
            <option key={f.id} value={String(f.id)}>
              {f.nombre_comercial ? `${f.nombre_comercial} (${f.nombre})` : f.nombre}
            </option>
          ))}
        </select>
        <Button
          size="sm"
          onClick={asignarFacturador}
          disabled={asignando}
          data-testid="factura-facturador-asignar-btn"
        >
          {asignando ? 'Asignando...' : 'Asignar'}
        </Button>
      </div>
      {serieSinFacturador > 1 && (
        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={aplicarSerie}
            onChange={(e) => setAplicarSerie(e.target.checked)}
            data-testid="factura-facturador-serie-check"
          />
          Aplicar también a las otras {serieSinFacturador - 1} facturas de la serie
          "{factura.serie}" que están sin facturador (requiere rol administrador)
        </label>
      )}
    </div>
  );

  const loadEnvioEmail = async () => {
    try {
      const res = await facturasAPI.getEmailInfo(id);
      setEnvioEmail(res.data?.email_enviado_at ? res.data : null);
    } catch (error) {
      setEnvioEmail(null);
    }
  };

  const loadEmisor = async () => {
    try {
      const res = await facturasAPI.getEmisor(id);
      setEmisor(res.data?.emisor || null);
      setEmisorOrigen(res.data?.origen || null);
      setEmisorIncompleto(res.data?.campos_incompletos || []);
      setSerieSinFacturador(res.data?.serie_sin_facturador || 0);
      setFacturadorSel(res.data?.facturador_id ? String(res.data.facturador_id) : '');
      try {
        const fs = await configuracionAPI.getFacturadores();
        setFacturadores(fs.data || []);
      } catch (e) {
        setFacturadores([]);
      }
      setLogoEmisor(res.data?.logo_url
        ? `${process.env.REACT_APP_BACKEND_URL || ''}${res.data.logo_url}`
        : null);
    } catch (error) {
      console.error('Error cargando datos de emisión:', error);
      setEmisor(null);
      setEmisorOrigen('sin_emisor');
    }
  };

  const loadFactura = async () => {
    try {
      setLoading(true);
      const res = await facturasAPI.getById(id);
      setFactura(res.data);
    } catch (error) {
      console.error('Error cargando factura:', error);
      toast({ title: "Error", description: "No se pudo cargar la factura", variant: "destructive" });
      navigate('/contabilidad');
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedData = async () => {
    try {
      const [contactosRes, expedientesRes] = await Promise.all([
        contactosAPI.getAll(),
        expedientesAPI.getAll()
      ]);
      setContactos(Array.isArray(contactosRes.data) ? contactosRes.data : (contactosRes.data?.data || []));
      setExpedientes(
        Array.isArray(expedientesRes.data)
          ? expedientesRes.data
          : (expedientesRes.data?.data || expedientesRes.data?.expedientes || [])
      );
    } catch (error) {
      console.error('Error cargando datos relacionados:', error);
    }
  };

  const handleConvertirAFactura = async () => {
    if (!window.confirm('¿Convertir este documento a factura definitiva?')) return;
    
    try {
      setSaving(true);
      await facturasAPI.convertirAFactura(id);
      toast({ title: "Convertido a factura", description: "El documento se ha convertido a factura definitiva" });
      loadFactura();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "No se pudo convertir", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleEmitirRectificativa = async (e) => {
    e.preventDefault();
    if ((rectForm.motivo || '').trim().length < 5) {
      toast({ title: 'Falta el motivo', description: 'El motivo de la rectificación es obligatorio (art. 15 RD 1619/2012).', variant: 'destructive' });
      return;
    }
    try {
      setSaving(true);
      const res = await facturasAPI.emitirRectificativa(id, {
        motivo: rectForm.motivo.trim(),
        tipo_rectificativa: rectForm.tipo_rectificativa,
        fecha_emision: rectForm.fecha_emision || null,
        copiar_conceptos: rectForm.copiar_conceptos,
        invertir_importes: rectForm.invertir_importes,
      });
      setIsRectDialogOpen(false);
      toast({ title: `Rectificativa ${res.data?.numero_factura || ''} emitida`, description: 'Serie propia de rectificativas. Revisa los importes antes de enviarla.' });
      navigate(`/contabilidad/factura/${res.data.id}`);
    } catch (error) {
      toast({ title: 'No se pudo emitir', description: error.response?.data?.detail || 'Error al emitir la rectificativa', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDescargarPdf = async () => {
    try {
      setSaving(true);
      const response = await facturasAPI.descargarPdf(id);
      
      // Crear blob y descargar
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${factura.numero_factura.replace(/\//g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({ title: "PDF descargado" });
    } catch (error) {
      toast({ title: "Error", description: "No se pudo generar el PDF", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleRegistrarPago = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      await facturasAPI.registrarPago(id, {
        importe: parseFloat(pagoForm.importe),
        metodo_pago: pagoForm.metodo_pago,
        referencia: pagoForm.referencia,
        descripcion: pagoForm.descripcion
      });
      
      toast({ title: "Pago registrado" });
      setIsPagoDialogOpen(false);
      setPagoForm({ importe: '', metodo_pago: 'TRANSFERENCIA BANCARIA', referencia: '', descripcion: '' });
      loadFactura();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "No se pudo registrar el pago", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleEliminarPago = async (pagoId) => {
    if (!window.confirm('¿Eliminar este pago?')) return;
    
    try {
      await facturasAPI.eliminarPago(id, pagoId);
      toast({ title: "Pago eliminado" });
      loadFactura();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar el pago", variant: "destructive" });
    }
  };

  const handleOpenEditDialog = () => {
    setEditForm({
      tipo: factura.tipo,
      cliente_id: factura.cliente_id?.toString() || '',
      expediente_id: factura.expediente_id?.toString() || '',
      serie: factura.serie || '',
      numero_factura: factura.numero_factura || '',
      codigo_expediente: factura.codigo_expediente || '',
      descripcion: factura.descripcion || '',
      forma_pago: factura.forma_pago || 'TRANSFERENCIA BANCARIA',
      fecha_emision: factura.fecha_emision?.split('T')[0] || '',
      fecha_vencimiento: factura.fecha_vencimiento?.split('T')[0] || '',
      porcentaje_iva: factura.porcentaje_iva || 21,
      porcentaje_retencion: factura.porcentaje_retencion || 0,
      notas: factura.notas || '',
      conceptos: factura.conceptos || [],
      suplidos: factura.suplidos || [],
      cotitulares: factura.cotitulares || []
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateFactura = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      await facturasAPI.update(id, {
        ...editForm,
        cliente_id: editForm.cliente_id ? parseInt(editForm.cliente_id) : null,
        expediente_id: editForm.expediente_id ? parseInt(editForm.expediente_id) : null,
        porcentaje_iva: parseFloat(editForm.porcentaje_iva),
        porcentaje_retencion: parseFloat(editForm.porcentaje_retencion),
        cotitulares_ids: (editForm.cotitulares || [])
          .map(c => (typeof c === 'object' ? c.contacto_id || c.id : c))
          .filter(Boolean),
      });
      
      toast({ title: "Factura actualizada" });
      setIsEditDialogOpen(false);
      loadFactura();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "No se pudo actualizar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const addConcepto = () => {
    setEditForm({
      ...editForm,
      conceptos: [...editForm.conceptos, {
        concepto: '',
        descripcion: '',
        unidades: 1,
        precio: 0,
        impuesto_pct: editForm.porcentaje_iva,
        retencion_pct: editForm.porcentaje_retencion,
        base_imponible: 0
      }]
    });
  };

  const updateConcepto = (index, field, value) => {
    const newConceptos = [...editForm.conceptos];
    newConceptos[index] = { ...newConceptos[index], [field]: value };
    
    // Recalcular base_imponible
    if (field === 'unidades' || field === 'precio') {
      const unidades = field === 'unidades' ? parseFloat(value) : parseFloat(newConceptos[index].unidades);
      const precio = field === 'precio' ? parseFloat(value) : parseFloat(newConceptos[index].precio);
      newConceptos[index].base_imponible = unidades * precio;
    }
    
    setEditForm({ ...editForm, conceptos: newConceptos });
  };

  const removeConcepto = (index) => {
    const newConceptos = editForm.conceptos.filter((_, i) => i !== index);
    setEditForm({ ...editForm, conceptos: newConceptos });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!factura) return null;

  const estadoInfo = ESTADOS[factura.estado] || ESTADOS.pendiente;
  const tipoInfo = TIPOS[factura.tipo] || TIPOS.proforma;
  const EstadoIcon = estadoInfo.icon;

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header con acciones */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/contabilidad')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <FileText className={`w-6 h-6 ${tipoInfo.color}`} />
              <h1 className="text-2xl font-bold">{tipoInfo.label} | {factura.numero_factura}</h1>
            </div>
            <Badge className={`mt-1 ${estadoInfo.color}`}>
              <EstadoIcon className="w-3 h-3 mr-1" />
              {estadoInfo.label}
            </Badge>
            <span className="ml-2"><FiscalStateBadge fiscalState={factura.fiscal_state} /></span>
          </div>
        </div>
        
        <div className="flex gap-2">
          {factura.tipo === 'factura' && (
            <Button
              variant="outline"
              className="border-amber-400 text-amber-700 hover:bg-amber-50"
              onClick={() => setIsRectDialogOpen(true)}
              disabled={saving}
              data-testid="emitir-rectificativa-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              EMITIR RECTIFICATIVA
            </Button>
          )}
          {factura.tipo !== 'factura' && factura.tipo !== 'rectificativa' && (
            <Button 
              onClick={handleConvertirAFactura} 
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              CONVERTIR A FACTURA
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleOpenEditDialog}
            disabled={factura.fiscal_state?.congelada}
            title={factura.fiscal_state?.congelada ? 'Factura congelada fiscalmente' : ''}
            data-testid="editar-factura-btn"
          >
            <Pencil className="w-4 h-4 mr-2" />
            EDITAR
          </Button>
          <Button variant="outline" onClick={handleDescargarPdf} disabled={saving}>
            <Download className="w-4 h-4 mr-2" />
            DESCARGAR/IMPRIMIR
          </Button>
          <EnviarFacturaEmailDialog
            factura={factura}
            onEnviado={() => { loadFactura(); loadEnvioEmail(); }}
          />
          {factura.estado !== 'pagada' && factura.estado !== 'anulada' && (
            <Button 
              onClick={() => {
                setPagoForm({ ...pagoForm, importe: factura.total_pendiente.toString() });
                setIsPagoDialogOpen(true);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              AÑADIR COBRO
            </Button>
          )}
        </div>
      </div>

      {/* Panel Verifactu / SIF */}
      {envioEmail && (
        <p
          className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-3"
          data-testid="factura-email-enviada-info"
        >
          Enviada al cliente el {new Date(envioEmail.email_enviado_at).toLocaleString('es-ES')} a{' '}
          <strong>{envioEmail.email_enviado_a}</strong>
          {envioEmail.email_enviado_por ? ` · por ${envioEmail.email_enviado_por}` : ''}
        </p>
      )}

      {factura.numero_rectificada && (
        <p
          className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3"
          data-testid="aviso-es-rectificativa"
        >
          <strong>Factura rectificativa</strong>{' '}
          {factura.tipo_rectificativa === 'I' ? 'por diferencias' : 'por sustitución'} de la factura{' '}
          <strong>{factura.numero_rectificada}</strong>
          {factura.motivo_rectificacion ? ` · Motivo: ${factura.motivo_rectificacion}` : ''}
          {factura.factura_rectificada_id && (
            <Button
              variant="link"
              className="h-auto p-0 ml-2 text-amber-900 underline"
              onClick={() => navigate(`/contabilidad/factura/${factura.factura_rectificada_id}`)}
              data-testid="ver-factura-rectificada-btn"
            >
              ver la factura rectificada
            </Button>
          )}
        </p>
      )}

      {(factura.rectificada_por || []).length > 0 && (
        <p
          className="text-sm text-purple-800 bg-purple-50 border border-purple-200 rounded-md p-3"
          data-testid="aviso-factura-rectificada"
        >
          Esta factura ha sido rectificada por{' '}
          {factura.rectificada_por.map((r, i) => (
            <span key={r.id}>
              {i > 0 && ', '}
              <Button
                variant="link"
                className="h-auto p-0 text-purple-900 underline"
                onClick={() => navigate(`/contabilidad/factura/${r.id}`)}
                data-testid={`ver-rectificativa-${r.id}`}
              >
                {r.numero_factura}
              </Button>
            </span>
          ))}
          . Ya no se puede modificar ni borrar.
        </p>
      )}

      <VerifactuPanel factura={factura} onChanged={loadFactura} />

      {/* Vista previa tipo factura */}
      <Card className="border-2 shadow-lg">
        <CardContent className="p-8">
          {/* Cabecera del documento */}
          <div className="flex justify-between mb-8 pb-6 border-b">
            <div data-testid="factura-emisor">
              {logoEmisor && (
                <img src={logoEmisor} alt="" className="h-14 mb-3 object-contain" />
              )}
              {emisor ? (
                <>
                  <h2 className="text-3xl font-bold text-blue-700 mb-2">
                    {emisor.rotulo || emisor.nombre}
                  </h2>
                  {emisor.nombre_fiscal && emisor.nombre_fiscal !== (emisor.rotulo || emisor.nombre) && (
                    <p className="text-sm text-gray-600">{emisor.nombre_fiscal}</p>
                  )}
                  {emisor.nif && <p className="text-sm text-gray-500">NIF / CIF: {emisor.nif}</p>}
                  {emisor.direccion && <p className="text-sm text-gray-500">{emisor.direccion}</p>}
                  {emisor.localidad && <p className="text-sm text-gray-500">{emisor.localidad}</p>}
                  {(emisor.provincia || emisor.pais) && (
                    <p className="text-sm text-gray-500">
                      {[emisor.provincia, emisor.pais].filter(Boolean).join(', ')}
                    </p>
                  )}
                  {(emisor.telefono || emisor.telefono2) && (
                    <p className="text-sm text-gray-500 mt-2">
                      {[emisor.telefono, emisor.telefono2].filter(Boolean).join(' / ')}
                    </p>
                  )}
                  {emisor.email && <p className="text-sm text-blue-600">{emisor.email}</p>}
                  {emisor.web && <p className="text-sm text-blue-600">{emisor.web}</p>}
                  {emisorOrigen === 'facturador' && (
                    <p className="text-xs text-gray-400 mt-2">
                      Datos actuales del facturador · edítalos en Configuración → Facturadores
                    </p>
                  )}
                  {cambiarEmisor ? selectorFacturador : (
                    <button
                      type="button"
                      onClick={() => setCambiarEmisor(true)}
                      className="text-xs text-blue-600 underline mt-2"
                      data-testid="factura-cambiar-facturador-btn"
                    >
                      Cambiar facturador emisor
                    </button>
                  )}
                  {emisorIncompleto.length > 0 && (
                    <p
                      className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-2 max-w-md"
                      data-testid="factura-emisor-incompleto"
                    >
                      <AlertTriangle className="w-3 h-3 inline mr-1" />
                      Faltan datos fiscales del emisor: {emisorIncompleto.join(', ')}.
                    </p>
                  )}
                </>
              ) : (
                <div
                  className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 max-w-md"
                  data-testid="factura-emisor-vacio"
                >
                  <p className="font-semibold flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Sin datos de emisión
                  </p>
                  <p className="mt-1">
                    No hay ningún facturador para la serie <strong>{factura.serie || '(vacía)'}</strong>.
                    Créalo o añade esa serie en Configuración → Facturadores.
                  </p>
                  {facturadores.length > 0 && selectorFacturador}
                  <Button
                    variant="outline" size="sm" className="mt-2"
                    onClick={() => navigate('/configuracion')}
                    data-testid="factura-emisor-configurar-btn"
                  >
                    Crear facturador en Configuración
                  </Button>
                </div>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm"><strong>{tipoInfo.label}:</strong> {factura.numero_factura}</p>
              <p className="text-sm"><strong>Serie:</strong> {factura.serie || '—'}</p>
              <p className="text-sm"><strong>Fecha:</strong> {formatDate(factura.fecha_emision)}</p>
              {factura.codigo_expediente && (
                <p className="text-sm"><strong>Cód. Exp:</strong> {factura.codigo_expediente}</p>
              )}
              <p className="text-sm"><strong>Forma de pago:</strong> {factura.forma_pago}</p>
            </div>
          </div>

          {/* Cliente */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500 mb-1">CLIENTE</p>
            <p className="font-bold text-lg">{factura.cliente_nombre}</p>
            {factura.cliente_nif && <p className="text-sm">CIF/NIF: {factura.cliente_nif}</p>}
            {factura.cliente_direccion && <p className="text-sm text-gray-600">{factura.cliente_direccion}</p>}
            {Array.isArray(factura.cotitulares) && factura.cotitulares.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200" data-testid="cotitulares-view">
                <p className="text-xs text-gray-500 mb-1">TAMBIÉN OBLIGADOS AL PAGO (informativo)</p>
                <ul className="text-sm text-gray-700 space-y-0.5">
                  {factura.cotitulares.map(c => (
                    <li key={c.id || c.contacto_id}>
                      • {c.nombre || `Contacto #${c.contacto_id}`}
                      {c.nif_cif && <span className="text-gray-500 font-mono"> ({c.nif_cif})</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Descripción */}
          {factura.descripcion && (
            <div className="mb-6">
              <p className="text-sm"><strong>Descripción:</strong> {factura.descripcion}</p>
            </div>
          )}

          {/* Conceptos facturables */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-blue-600 mb-4">Conceptos facturables</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Concepto</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead className="text-right">Unidades</TableHead>
                  <TableHead className="text-right">Precio</TableHead>
                  <TableHead className="text-right">Impuesto(%)</TableHead>
                  <TableHead className="text-right">Retención(%)</TableHead>
                  <TableHead className="text-right">Base Imponible</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(factura.conceptos || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-gray-500">No hay conceptos</TableCell>
                  </TableRow>
                ) : (
                  factura.conceptos.map((c, i) => {
                    const uds = parseFloat(c.unidades) || 0;
                    const precio = parseFloat(c.precio) || 0;
                    const base = parseFloat(c.base_imponible) || (uds * precio);
                    return (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{c.concepto}</TableCell>
                      <TableCell className="text-sm text-gray-600 max-w-xs">{c.descripcion}</TableCell>
                      <TableCell className="text-right">{uds.toFixed(2)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(precio)}</TableCell>
                      <TableCell className="text-right">{c.impuesto_pct || 21}%</TableCell>
                      <TableCell className="text-right">{c.retencion_pct || 0}%</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(base)}</TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Suplidos */}
          {(factura.suplidos || []).length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-blue-600 mb-4">Suplidos</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Concepto</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead className="text-right">Unidades</TableHead>
                    <TableHead className="text-right">Precio</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {factura.suplidos.map((s, i) => {
                    const uds = parseFloat(s.unidades) || 0;
                    const precio = parseFloat(s.precio) || 0;
                    const total = parseFloat(s.total) || (uds * precio);
                    return (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{s.concepto}</TableCell>
                      <TableCell className="text-sm text-gray-600">{s.descripcion}</TableCell>
                      <TableCell className="text-right">{uds.toFixed(2)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(precio)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(total)}</TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Totales */}
          <div className="flex justify-end">
            <div className="w-80">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Base Imponible</span>
                  <span>{formatCurrency(factura.base_imponible)}</span>
                </div>
                <div className="flex justify-between">
                  <span>% IVA/IGIC ({factura.porcentaje_iva}%)</span>
                  <span>{formatCurrency(factura.importe_iva)}</span>
                </div>
                {factura.porcentaje_retencion > 0 && (
                  <div className="flex justify-between">
                    <span>% Retención ({factura.porcentaje_retencion}%)</span>
                    <span className="text-red-600">-{formatCurrency(factura.importe_retencion)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-2">
                  <span>Total + IVA/IGIC - Retenciones:</span>
                  <span className="font-bold">{formatCurrency(factura.total - factura.total_suplidos)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total suplidos:</span>
                  <span>{formatCurrency(factura.total_suplidos)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>TOTAL:</span>
                  <span>{formatCurrency(factura.total)}</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Total pagado:</span>
                  <span>{formatCurrency(factura.total_pagado)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-blue-600 border-t pt-2">
                  <span>Total a liquidar:</span>
                  <span>{formatCurrency(factura.total_pendiente)}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pagos a cuenta */}
      {(factura.pagos || []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5" /> Pagos a cuenta
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Factura</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead className="text-right">Importe</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {factura.pagos.map((pago) => (
                  <TableRow key={pago.id}>
                    <TableCell>{factura.numero_factura}</TableCell>
                    <TableCell>{formatDateTime(pago.fecha)}</TableCell>
                    <TableCell>{pago.descripcion || pago.metodo_pago}</TableCell>
                    <TableCell className="text-right font-medium text-green-600">{formatCurrency(pago.importe)}</TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEliminarPago(pago.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Dialog de pago */}
      <Dialog open={isPagoDialogOpen} onOpenChange={setIsPagoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Cobro</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRegistrarPago} className="space-y-4">
            <div>
              <Label>Importe (€) *</Label>
              <Input
                type="number"
                step="0.01"
                max={factura.total_pendiente}
                value={pagoForm.importe}
                onChange={(e) => setPagoForm({ ...pagoForm, importe: e.target.value })}
                required
              />
              <p className="text-sm text-gray-500 mt-1">Pendiente: {formatCurrency(factura.total_pendiente)}</p>
            </div>
            <div>
              <Label>Método de pago</Label>
              <Select 
                value={pagoForm.metodo_pago} 
                onValueChange={(v) => setPagoForm({ ...pagoForm, metodo_pago: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="TRANSFERENCIA BANCARIA">Transferencia bancaria</SelectItem>
                  <SelectItem value="EFECTIVO">Efectivo</SelectItem>
                  <SelectItem value="TARJETA">Tarjeta</SelectItem>
                  <SelectItem value="BIZUM">Bizum</SelectItem>
                  <SelectItem value="CHEQUE">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Referencia</Label>
              <Input
                value={pagoForm.referencia}
                onChange={(e) => setPagoForm({ ...pagoForm, referencia: e.target.value })}
                placeholder="Nº transferencia, etc."
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={pagoForm.descripcion}
                onChange={(e) => setPagoForm({ ...pagoForm, descripcion: e.target.value })}
                placeholder="Notas del pago..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsPagoDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Registrar Cobro
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de edición */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar {tipoInfo.label}</DialogTitle>
          </DialogHeader>
          {editForm && (
            <form onSubmit={handleUpdateFactura} className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <Select value={editForm.tipo} onValueChange={(v) => setEditForm({ ...editForm, tipo: v })}>
                    <SelectTrigger data-testid="edit-tipo-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="proforma">Proforma</SelectItem>
                      <SelectItem value="factura">Factura</SelectItem>
                      <SelectItem value="presupuesto">Presupuesto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nº Factura</Label>
                  <Input
                    data-testid="edit-numero-factura"
                    value={editForm.numero_factura || ''}
                    onChange={(e) => setEditForm({ ...editForm, numero_factura: e.target.value })}
                    placeholder="Ej: 01/2026-EC"
                  />
                  <p className="text-xs text-gray-500 mt-1">Editable. Debe ser único.</p>
                </div>
                <div>
                  <Label>Cliente</Label>
                  <ClienteComboboxDetalle
                    value={editForm.cliente_id}
                    onChange={(v) => setEditForm({ ...editForm, cliente_id: v })}
                    contactos={contactos}
                    onSelectedChange={setSelectedClienteData}
                  />
                  {selectedClienteData && (
                    <div className="mt-2 p-3 rounded-md border border-green-200 bg-green-50 text-xs space-y-0.5" data-testid="cliente-datos-card">
                      <div className="font-semibold text-green-900">
                        {selectedClienteData.nombre_completo || selectedClienteData.nombre}
                      </div>
                      {selectedClienteData.nif_cif && (
                        <div className="text-green-800">NIF/CIF: <span className="font-mono">{selectedClienteData.nif_cif}</span></div>
                      )}
                      {selectedClienteData.email && (
                        <div className="text-green-800 truncate">{selectedClienteData.email}</div>
                      )}
                      {(selectedClienteData.telefono || selectedClienteData.telefono_movil) && (
                        <div className="text-green-800">Tel: {selectedClienteData.telefono || selectedClienteData.telefono_movil}</div>
                      )}
                      {selectedClienteData.direccion && (
                        <div className="text-green-800 truncate">
                          {selectedClienteData.direccion}{selectedClienteData.codigo_postal ? `, ${selectedClienteData.codigo_postal}` : ''}{selectedClienteData.ciudad ? ` ${selectedClienteData.ciudad}` : ''}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Expediente (opcional)</Label>
                  <ExpedienteComboboxDetalle
                    value={editForm.expediente_id}
                    onChange={(v) => setEditForm({ ...editForm, expediente_id: v })}
                    expedientes={expedientes}
                    onSelectedChange={setSelectedExpedienteData}
                  />
                  {selectedExpedienteData && (
                    <div className="mt-2 p-3 rounded-md border border-green-200 bg-green-50 text-xs space-y-0.5" data-testid="expediente-datos-card">
                      <div className="font-semibold text-green-900 truncate">
                        {selectedExpedienteData.numero_expediente} — {selectedExpedienteData.titulo}
                      </div>
                      {selectedExpedienteData.cliente_nombre && (
                        <div className="text-green-800">Cliente: {selectedExpedienteData.cliente_nombre}</div>
                      )}
                      {selectedExpedienteData.tipo_procedimiento && (
                        <div className="text-green-800">Procedimiento: {selectedExpedienteData.tipo_procedimiento}</div>
                      )}
                      {selectedExpedienteData.estado && (
                        <div className="text-green-800">Estado: {selectedExpedienteData.estado}</div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Fecha emisión</Label>
                  <Input
                    type="date"
                    value={editForm.fecha_emision}
                    onChange={(e) => setEditForm({ ...editForm, fecha_emision: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Fecha vencimiento</Label>
                  <Input
                    type="date"
                    value={editForm.fecha_vencimiento}
                    onChange={(e) => setEditForm({ ...editForm, fecha_vencimiento: e.target.value })}
                  />
                </div>
              </div>

              {/* Co-titulares informativos */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <CotitularesSelector
                  value={editForm.cotitulares || []}
                  onChange={(list) => setEditForm({ ...editForm, cotitulares: list })}
                  excludeIds={editForm.cliente_id ? [editForm.cliente_id] : []}
                />
              </div>

              <div>
                <Label>Descripción</Label>
                <Input
                  value={editForm.descripcion}
                  onChange={(e) => setEditForm({ ...editForm, descripcion: e.target.value })}
                  placeholder="Ej: SERVICIOS JURIDICOS"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Forma de pago</Label>
                  <Select value={editForm.forma_pago} onValueChange={(v) => setEditForm({ ...editForm, forma_pago: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TRANSFERENCIA BANCARIA">Transferencia bancaria</SelectItem>
                      <SelectItem value="EFECTIVO">Efectivo</SelectItem>
                      <SelectItem value="TARJETA">Tarjeta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>% IVA</Label>
                  <Input
                    type="number"
                    value={editForm.porcentaje_iva}
                    onChange={(e) => setEditForm({ ...editForm, porcentaje_iva: e.target.value })}
                  />
                </div>
                <div>
                  <Label>% Retención IRPF</Label>
                  <Input
                    type="number"
                    value={editForm.porcentaje_retencion}
                    onChange={(e) => setEditForm({ ...editForm, porcentaje_retencion: e.target.value })}
                  />
                </div>
              </div>

              {/* Conceptos */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-medium">Conceptos facturables</h4>
                  <Button type="button" size="sm" onClick={addConcepto}>
                    <Plus className="w-4 h-4 mr-1" /> Añadir
                  </Button>
                </div>
                {(Array.isArray(editForm.conceptos) ? editForm.conceptos : []).map((c, i) => (
                  <div key={i} className="grid grid-cols-6 gap-2 mb-2 items-end">
                    <Input
                      placeholder="Concepto"
                      value={c.concepto}
                      onChange={(e) => updateConcepto(i, 'concepto', e.target.value)}
                    />
                    <Input
                      placeholder="Descripción"
                      value={c.descripcion}
                      onChange={(e) => updateConcepto(i, 'descripcion', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Uds"
                      value={c.unidades}
                      onChange={(e) => updateConcepto(i, 'unidades', e.target.value)}
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Precio"
                      value={c.precio}
                      onChange={(e) => updateConcepto(i, 'precio', e.target.value)}
                    />
                    <Input
                      type="number"
                      readOnly
                      value={(c.base_imponible || c.unidades * c.precio).toFixed(2)}
                      className="bg-gray-50"
                    />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeConcepto(i)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>

              <div>
                <Label>Observaciones</Label>
                <Textarea
                  value={editForm.notas}
                  onChange={(e) => setEditForm({ ...editForm, notas: e.target.value })}
                  rows={3}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Guardar Cambios
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Emitir factura rectificativa — serie propia (art. 6.1.a RD 1619/2012) */}
      <Dialog open={isRectDialogOpen} onOpenChange={setIsRectDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="rectificativa-dialog">
          <DialogHeader>
            <DialogTitle>Emitir factura rectificativa</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEmitirRectificativa} className="space-y-4">
            <p className="text-sm text-gray-600">
              Se emitirá con un número de la <strong>serie propia de rectificativas</strong>{' '}
              (R-…), sin tocar la numeración de tus facturas, y rectificará a{' '}
              <strong>{factura.numero_factura}</strong>.
            </p>
            <div>
              <Label>Tipo de rectificación</Label>
              <Select
                value={rectForm.tipo_rectificativa}
                onValueChange={(v) => setRectForm({ ...rectForm, tipo_rectificativa: v })}
              >
                <SelectTrigger data-testid="rect-tipo-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="S">Por sustitución (la rectificativa sustituye a la original)</SelectItem>
                  <SelectItem value="I">Por diferencias (solo la diferencia)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {rectForm.tipo_rectificativa === 'S'
                  ? 'La factura original quedará marcada como rectificada y sin pendiente de cobro.'
                  : 'La factura original sigue vigente; la rectificativa recoge solo la diferencia.'}
              </p>
            </div>
            <div>
              <Label>Motivo de la rectificación *</Label>
              <Textarea
                value={rectForm.motivo}
                onChange={(e) => setRectForm({ ...rectForm, motivo: e.target.value })}
                rows={3}
                placeholder="Ej.: error en la base imponible del concepto de honorarios"
                data-testid="rect-motivo-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Obligatorio: debe constar en la factura (art. 15 RD 1619/2012).
              </p>
            </div>
            <div>
              <Label>Fecha de emisión</Label>
              <Input
                type="date"
                value={rectForm.fecha_emision}
                onChange={(e) => setRectForm({ ...rectForm, fecha_emision: e.target.value })}
                data-testid="rect-fecha-input"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={rectForm.copiar_conceptos}
                onChange={(e) => setRectForm({ ...rectForm, copiar_conceptos: e.target.checked })}
                data-testid="rect-copiar-conceptos"
              />
              Copiar los conceptos de la factura original
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={rectForm.invertir_importes}
                onChange={(e) => setRectForm({ ...rectForm, invertir_importes: e.target.checked })}
                data-testid="rect-invertir-importes"
              />
              Poner los importes en negativo (abono)
            </label>
            <p className="text-xs text-gray-500">
              Después podrás editar los conceptos e importes de la rectificativa antes de enviarla.
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsRectDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} className="bg-amber-600 hover:bg-amber-700"
                      data-testid="rect-emitir-btn">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Emitir rectificativa
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FacturaDetalle;
